import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { isNumber } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useDebounce, useFeatureFlags } from '@node-space/hooks'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  DataListItem,
  EmptyState,
  Input,
  Radio,
  RadioOption,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { Lottie } from 'components/Animations/SimpleLottie'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import {
  PayoutsFeeEstimateParams,
  usePayoutsFeeEstimateQuery,
} from 'hooks/queries/usePayoutsFeeEstimateQuery'
import { getConfirmPayeeBeneficiaryParams } from 'pages/Contacts/utils/fiatInstruments'
import { beneficiaryToUpdateAtom } from 'pages/ManageBeneficiaries/EditBeneficiary/atoms.ts/beneficiaryToUpdateAtom'
import { EditBeneficiarySheet } from 'pages/ManageBeneficiaries/EditBeneficiary/EditBeneficiarySheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import {
  BaseErrorResponse,
  BeneficiaryDetails,
  BeneficiaryTransferDestination,
  PaymentMethods,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { ConfirmPayeeBeneficiaryParams, ConfirmPayeeMatchStatuses } from 'types/payoutsV2'
import { Currencies } from 'types/types'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getBeneficiaryName } from 'utils/beneficiaries'
import track from 'utils/tracker'
import {
  formatString,
  generateReference,
  roundNumberWithCommas,
  stringToDecimal,
} from 'utils/utils'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import { FeeErrors, MAX_GBP_PAYMENT_AMOUNT } from '../constants'
import { FiatSendFormKeys } from '../form/fiatSendFormSchema'
import { useConfirmBeneficiaryCallout } from '../hooks/useConfirmBeneficiaryCallout'
import { ConfirmationOfPayeeCalloutTypes } from '../types'
import { getConfirmationOfPayeeName, getUnifiedPayoutRequestV2 } from '../utils'
import { ConfirmationOfPayeeCallout } from './ConfirmationOfPayeeCallout'

interface PaymentDetailsProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
}

const SEPA_CREDIT_INSTANT_TRANSFER_LIMIT = 100000

export const PaymentDetails = ({ setStep }: PaymentDetailsProps) => {
  const { t } = useTranslation()
  const { enableAchSameDayPayments, enableCustomerFee, enablePayoutPaymentMethodSelection } =
    useFeatureFlags()

  const [walletAction] = useContext(WalletActionContext)

  const sourceWalletAtom = useAtomValue(selectedWalletAtom)
  const [beneficiaryAtom, setBeneficiaryAtom] = useAtom(selectedFiatBeneficiaryAtom)
  const formAtom = useAtomValue(fiatSendFormAtom)
  const setBeneficiaryToUpdate = useSetAtom(beneficiaryToUpdateAtom)

  const [showErrorState, _setShowErrorState] = useState(false)
  const [isAutoPaymentMethodSelected, setIsAutoPaymentMethodSelected] = useState(true)
  const [feeErrorMessage, setFeeErrorMessage] = useState('')

  const { formValues, errors, register, setValue, trigger, setError, clearErrors, formState } =
    formAtom || {}

  const debouncedSendAmount = useDebounce(formValues?.amount, 500)
  const debouncedPaymentReference = useDebounce(formValues?.paymentReference, 500)

  const [showEditBeneficiarySheet, setShowEditBeneficiarySheet] = useState(false)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_DETAILS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    setValue('twoFactorCode', '')
  }, [])

  useEffect(() => {
    const fallbackPaymentReference = generateReference()

    setValue(
      'paymentReference',
      formValues?.paymentReference ||
        beneficiaryAtom?.beneficiaryPaymentReference ||
        fallbackPaymentReference
    )
  }, [beneficiaryAtom])

  useEffect(() => {
    if (isAutoPaymentMethodSelected) {
      setValue('paymentMethod', '')
    }
  }, [isAutoPaymentMethodSelected])

  const beneficiaryDetails: ConfirmPayeeBeneficiaryParams = useMemo(() => {
    return getConfirmPayeeBeneficiaryParams(beneficiaryAtom)
  }, [beneficiaryAtom])

  const { confirmPayeeData, confirmationOfPayeeCallout, isConfirmingPayee, editBeneficiary } =
    useConfirmBeneficiaryCallout({
      beneficiaryDetails,
      isPayout: true,
    })

  useEffect(() => {
    if (confirmPayeeData?.id) {
      setValue('beneficiaryVerificationId', confirmPayeeData.id)
    }
  }, [confirmPayeeData])

  const request = getUnifiedPayoutRequestV2(formValues, sourceWalletAtom, beneficiaryAtom)
  const payoutFeeEstimateParams: PayoutsFeeEstimateParams = {
    request,
    isEnabled: false,
  }

  const isBenficiaryPaymentMethodACH = beneficiaryAtom?.paymentMethod === USDBeneficiaryOptions.ACH

  const {
    isFetching: isFetchingFee,
    refetch: fetchFee,
    error: feeError,
  } = usePayoutsFeeEstimateQuery(payoutFeeEstimateParams)

  useEffect(() => {
    setFeeErrorMessage('')
    if (feeError) {
      const feeErrorResponse = feeError as BaseErrorResponse

      if (feeErrorResponse?.data?.code === FeeErrors.BVNK_0000) {
        trigger('amount')
        return
      }

      if (feeErrorResponse?.data?.code === FeeErrors.BENEFICIARY_VALIDATION_ERROR) {
        const errorMessage =
          feeErrorResponse?.data?.details?.errors?.requestBody?.join('. ') ||
          feeErrorResponse?.data?.message ||
          t('fetchingFeeError')

        setFeeErrorMessage(errorMessage)
        return
      }

      setError('amount', { message: feeErrorResponse?.data?.message })

      track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.ERROR,
        step: FiatSendSteps.DETAILS,
        errorType: 'FEE_ERROR',
        error: feeErrorResponse,
      })

      return
    }

    clearErrors()
  }, [feeError])

  const walletTitle = useMemo(() => {
    return `${sourceWalletAtom?.walletName} | ${formatString(
      t('balanceWithCurrency'),
      roundNumberWithCommas(sourceWalletAtom?.balance),
      sourceWalletAtom?.currency
    )}`
  }, [sourceWalletAtom])

  const feeText = useMemo(() => {
    if ((!debouncedSendAmount || feeError || !formValues?.fee) && !isFetchingFee) {
      return `0.00 ${sourceWalletAtom?.currency}`
    }

    if (isFetchingFee) {
      return t('wallets.fiatSend.calculatingFee')
    }

    return `${roundNumberWithCommas(formValues?.fee)} ${sourceWalletAtom?.currency}`
  }, [isFetchingFee, formValues?.fee, sourceWalletAtom, debouncedSendAmount, feeError])

  const isPaymentMethodSelectionAvailable = useMemo(() => {
    if (
      !enablePayoutPaymentMethodSelection ||
      beneficiaryAtom?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    )
      return false

    switch (formValues?.currencyCode) {
      case Currencies.EUR:
        return true
      case Currencies.GBP:
      case Currencies.USD:
      case Currencies.AED:
      case Currencies.ZAR:
      default:
        return false
    }
  }, [formValues?.currencyCode])

  const paymentMethodOptions: RadioOption[] = useMemo(() => {
    switch (formValues?.currencyCode) {
      case Currencies.EUR:
        return [
          {
            label: `${t('wallets.fiatSend.autoPaymentMethod')} (${t('wallets.fiatSend.recommended')})`,
            value: '',
            secondaryLabel: t('wallets.fiatSend.autoPaymentMethodDescription'),
            disabled: false,
          },
          {
            label: t('wallets.fiatSend.sepaInstant'),
            value: PaymentMethods.SEPA_INSTANT,
            secondaryLabel: t('wallets.fiatSend.sepaInstantDescription'),
            disabled: Number(formValues?.amount) > 100000,
          },
          {
            label: t('wallets.fiatSend.sepaCredit'),
            value: PaymentMethods.SEPA,
            secondaryLabel: t('wallets.fiatSend.sepaCreditDescription'),
          },
        ]
      case Currencies.GBP:
      case Currencies.USD:
      case Currencies.AED:
      case Currencies.ZAR:
      default:
        return []
    }
  }, [formValues?.currencyCode, formValues?.amount])

  useEffect(() => {
    // SEPA Credit Instant Transfer limits are 100,000 EUR
    const isValidNumberAmount = isNumber(Number(formValues?.amount))
    const isSepaInstantCreditPaymentMethod =
      formValues?.paymentMethod === PaymentMethods.SEPA_INSTANT
    const isOverSepaCreditThreshold =
      Number(formValues?.amount) > SEPA_CREDIT_INSTANT_TRANSFER_LIMIT
    const shouldResetPaymentMethod =
      isValidNumberAmount &&
      isPaymentMethodSelectionAvailable &&
      isSepaInstantCreditPaymentMethod &&
      isOverSepaCreditThreshold

    if (shouldResetPaymentMethod) {
      setValue('paymentMethod', '')
    }
  }, [formValues?.amount, isPaymentMethodSelectionAvailable, formValues?.paymentMethod])

  useEffect(() => {
    if (!formValues?.paymentMethod && !!paymentMethodOptions.length) {
      setValue('paymentMethod', paymentMethodOptions?.[0]?.value)
    }
  }, [formValues?.paymentMethod, paymentMethodOptions])

  useEffect(() => {
    if (formValues?.achSameDay) {
      setValue('paymentMethod', PaymentMethods.ACH_SAME_DAY)
      return
    }

    setValue('paymentMethod', '')
  }, [formValues?.achSameDay])

  const onInputChange = (field: FiatSendFormKeys, event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(field, event.target.value)
  }

  const handleOnContinueClick = async () => {
    clearErrors()
    const isValid = await trigger(['amount', 'paymentReference'])

    if (isValid) {
      track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_DETAILS_CONTINUE, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.CLICK,
        amount: formValues?.amount,
        paymentReference: formValues?.paymentReference,
        fee: formValues.fee,
        walletId: sourceWalletAtom?.walletId,
        beneficiaryReference: beneficiaryAtom?.reference,
        ...(formValues?.paymentMethod && { paymentMethod: formValues.paymentMethod }),
        ...(formValues?.achSameDay && { achSameDay: formValues.achSameDay }),
        ...(formValues?.customerFee && { customerFee: formValues.customerFee }),
      })
      setStep(FiatSendSteps.CONFIRMATION)
    }
  }

  const handleOnCancelClick = () => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_CANCEL, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      step: FiatSendSteps.DETAILS,
    })
    walletAction?.onClose()
  }

  const validateAmount = async (amount: string) => {
    setFeeErrorMessage('')
    const error = await checkMaxAmount(amount)

    if (error) {
      setError('amount', { message: error })
    } else {
      clearErrors('amount')
    }
  }

  const checkMaxAmount = async (amount: string) => {
    const customerFeeAmount = Number(formValues?.customerFee ?? 0)
    const total = Number(amount ?? 0) + customerFeeAmount

    const feeData = await fetchFee()
    const { availablePayoutAmount, fees } = feeData?.data || {}
    const { processingFee } = fees || {}

    const amountErrorMaximumFees =
      processingFee &&
      sourceWalletAtom &&
      formatString(
        t('amountErrorMaximumFees'),
        roundNumberWithCommas(availablePayoutAmount?.amount),
        sourceWalletAtom?.currency
      )

    if (Number(total) > (availablePayoutAmount?.amount ?? 0)) {
      return amountErrorMaximumFees
    }

    setValue('fee', processingFee?.amount?.toString() ?? '')
  }

  const amountOnBlurHandler = async (event: React.FocusEvent<HTMLInputElement, Element>) => {
    const isValid = await trigger('amount')

    if (!isValid) {
      return
    }

    const amount = Number(event?.target?.value ?? 0)
    const customerFeeAmount = Number(formValues?.customerFee ?? 0)
    const total = amount + customerFeeAmount
    const isGbpPayment = sourceWalletAtom?.currency === Currencies.GBP

    if (!isNaN(total) && isGbpPayment && total > MAX_GBP_PAYMENT_AMOUNT) {
      setError('amount', { message: t('wallets.fiatSend.maximumGbpAmount') })
      return
    }

    event?.target?.value && validateAmount(stringToDecimal(event?.target?.value))
  }

  const paymentReferenceOnBlurHandler = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (formState?.errors?.paymentReference) {
      return
    }

    if (event?.target?.value) {
      setValue('paymentReference', event?.target?.value)
    }
  }

  useEffect(() => {
    const shouldRefetchFee = !!debouncedSendAmount && !isNaN(Number(debouncedSendAmount))

    if (shouldRefetchFee) {
      validateAmount(debouncedSendAmount)
    }
  }, [
    debouncedSendAmount,
    formValues?.achSameDay,
    formValues?.paymentMethod,
    debouncedPaymentReference,
  ])

  const isContinueButtonDisabled = useMemo(() => {
    return !formValues.amount || !formValues.paymentReference || !!errors?.amount
  }, [formValues.amount, formValues.paymentReference, errors?.amount])

  const isCustomerFeeEnabled = useMemo(() => {
    return !!walletAction?.meta?.customerWallet && enableCustomerFee
  }, [walletAction?.meta?.customerWallet])

  const handleEditBeneficiary = useCallback(() => {
    const isPartialMatch = confirmPayeeData?.matchStatus === ConfirmPayeeMatchStatuses.PARTIAL_MATCH

    setBeneficiaryToUpdate({
      beneficiary: {
        ...beneficiaryAtom,
        // If partial match, prefill the beneficiary name with the actual name
        ...(isPartialMatch && getConfirmationOfPayeeName(beneficiaryAtom, confirmPayeeData)),
      },
      isNameUpdated: true,
      onBack: () => {
        setBeneficiaryToUpdate(null)
      },
      onClose: () => {
        setShowEditBeneficiarySheet(false)
        setBeneficiaryToUpdate(null)
      },
      onUpdateBeneficiary: (beneficiary: BeneficiaryDetails) => {
        setBeneficiaryAtom(beneficiary)
      },
    })

    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_UPDATE_BENEFICIARY, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
    setShowEditBeneficiarySheet(true)
  }, [setBeneficiaryToUpdate, confirmPayeeData])

  return (
    <Box paddingT={24} className="max-h-[75vh]">
      {showErrorState && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
          description={
            <>
              {t('requestErrors.comeBackLater')}{' '}
              <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                {t('requestErrors.refreshPage')}
              </Button>
            </>
          }
        />
      )}
      {!showErrorState && (
        <Box paddingB={16}>
          <Card variant="static" emphasis full>
            <DataListItem
              id="wallet"
              title={t('wallets.fiatSend.sendingFromWallet')}
              BodyAvatar={<Avatar type="currency" name={sourceWalletAtom?.currency} size="small" />}
              body={walletTitle}
              layout={'vertical'}
            />

            <DataListItem
              id="beneficiary"
              title={t('wallets.fiatSend.sendingTo')}
              BodyAvatar={<Avatar type="entity" name={'A'} size="small" />}
              body={`${getBeneficiaryName(beneficiaryAtom)} | ${ellipseMiddleText(beneficiaryAtom?.fiat?.accountNumber, 6, 6)}`}
              layout={'vertical'}
            />
          </Card>

          {isConfirmingPayee && (
            <Box paddingT={16}>
              <RowsSkeletonLoader rows={6} />
            </Box>
          )}

          {feeErrorMessage && (
            <Box paddingT={16}>
              <Callout variant="error">{feeErrorMessage}</Callout>
            </Box>
          )}

          {!isConfirmingPayee && (
            <>
              {confirmationOfPayeeCallout && (
                <ConfirmationOfPayeeCallout
                  type={ConfirmationOfPayeeCalloutTypes.PAYOUT}
                  confirmationOfPayeeCallout={confirmationOfPayeeCallout}
                  confirmPayeeData={confirmPayeeData}
                  editBeneficiary={editBeneficiary}
                  handleEditBeneficiary={handleEditBeneficiary}
                />
              )}

              <Box flexCol paddingY={16} width="full" gapY={16}>
                <Input
                  {...register('amount')}
                  label="Amount"
                  placeholderText={'0'}
                  onChange={e => onInputChange('amount', e)}
                  testId="fiat-send-amount"
                  type="text"
                  inputMode="numeric"
                  onBlur={amountOnBlurHandler}
                  loading={isFetchingFee}
                  rightSuffixText={sourceWalletAtom?.currency}
                  error={!!errors.amount?.message}
                  errorText={errors.amount?.message}
                />

                {isCustomerFeeEnabled && (
                  <Input
                    {...register('customerFee')}
                    label={t('wallets.fiatSend.customerFee')}
                    placeholderText={'0'}
                    onChange={e => onInputChange('customerFee', e)}
                    testId="fiat-customer-fee"
                    type="text"
                    inputMode="numeric"
                    optionalText={t('optional')}
                    rightSuffixText={sourceWalletAtom?.currency}
                    error={!!errors.customerFee?.message}
                    errorText={errors.customerFee?.message}
                  />
                )}

                <Input
                  {...register('paymentReference')}
                  label="Payment reference"
                  onChange={e => onInputChange('paymentReference', e)}
                  onBlur={paymentReferenceOnBlurHandler}
                  testId="fiat-send-payment-reference"
                  error={!!errors.paymentReference?.message}
                  errorText={errors.paymentReference?.message}
                />

                {isPaymentMethodSelectionAvailable && (
                  <Radio
                    {...register('paymentMethod')}
                    name="paymentMethod"
                    border={false}
                    key={`${formValues?.paymentMethod}`}
                    testId="beneficiary-entity-type"
                    options={paymentMethodOptions}
                    value={formValues?.paymentMethod}
                    error={errors?.paymentMethod?.message}
                    onChange={value => setValue('paymentMethod', value)}
                    label={t('paymentMethod')}
                  />
                )}

                {isBenficiaryPaymentMethodACH && enableAchSameDayPayments && (
                  <Box paddingT={12} paddingB={8}>
                    <Checkbox
                      {...register('achSameDay')}
                      id="ach-sameday-checkbox"
                      testId="ach-sameday-checkbox"
                      checked={formValues?.achSameDay}
                      onCheckedChange={() => setValue('achSameDay', !formValues?.achSameDay)}
                      label={t('wallets.fiatSend.makeAchSameDayPayment')}
                    >
                      <Box flex>
                        <p className="text-body-regular-small text-foreground-faint pr-1">
                          {t('wallets.fiatSend.subjectToCutOffTimes')}
                        </p>
                        <p className="text-body-regular-small text-foreground-secondary pr-1">
                          <ExternalLink href={process.env.VA_CUT_OFF_TIMES_LEARN_MORE}>
                            {t('learnMore')}
                          </ExternalLink>
                        </p>
                      </Box>
                    </Checkbox>
                  </Box>
                )}

                <DataListItem
                  key={feeText}
                  id="transaction-fee"
                  title={t('wallets.fiatSend.transactionFee')}
                  body={feeText}
                  layout={'vertical'}
                />
              </Box>

              <Box flex justify="between" paddingT={20}>
                <Button testId="cancel-btn" variant="secondary" onClick={handleOnCancelClick}>
                  {t('cancel')}
                </Button>
                <Button
                  testId="continue-btn"
                  onClick={handleOnContinueClick}
                  disabled={isContinueButtonDisabled}
                  loading={isFetchingFee}
                >
                  {t('continue')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
      <EditBeneficiarySheet isOpen={showEditBeneficiarySheet} isNestedSheet={true} />
    </Box>
  )
}
